import type { Character, Hotspot, Location } from "../manifest/schema.js";
import { hotspotDirection } from "../nav/direction.js";
import { arrowIcon } from "./arrows.js";
import { h, clear } from "./dom.js";
import { hotspotIcon, iconSvg } from "./icons.js";

export interface StageHandlers {
  onHotspot: (hotspot: Hotspot, location: Location) => void;
  onCharacter: (character: Character) => void;
  onBackgroundClick: () => void;
}

/** The fixed 16:9 viewport: still image, video layer, hotspots, characters. */
export class Stage {
  readonly root = h("div", { class: "stage", "data-testid": "stage" });
  readonly videoLayer = h("div", { class: "video-layer" });
  private readonly still = h("img", { class: "still", alt: "", draggable: "false" });
  private readonly hotspotLayer = h("div", { class: "hotspots" });
  private readonly characterLayer = h("div", { class: "characters" });
  private readonly liveLayer = h("div", { class: "live-layer" });
  private live: { video: HTMLVideoElement; idle: string; ids: Set<string> } | null = null;

  constructor(
    private readonly mediaUrl: (rel: string) => string,
    private readonly handlers: StageHandlers,
    private readonly opts: { reducedMotion?: boolean; supportsWebm?: boolean } = {}
  ) {
    this.root.append(this.still, this.liveLayer, this.videoLayer, this.characterLayer, this.hotspotLayer);
    this.root.addEventListener("click", (e) => {
      if (e.target === this.root || e.target === this.still || e.target === this.videoLayer) this.handlers.onBackgroundClick();
    });
  }

  /** Swap the still. Resolves once the new image is decoded so the swap never flashes. */
  async setStill(location: Location): Promise<void> {
    this.root.dataset.location = location.id;
    const url = `${this.mediaUrl(location.still)}.jpg`;
    this.still.alt = location.alt;
    this.still.src = url;
    try {
      await this.still.decode();
    } catch {
      /* missing or undecodable still: keep going, alt text is shown */
    }
  }

  /** Render hotspots and characters for a location (interactive layer). */
  showInteractive(location: Location, characters: readonly Character[]): void {
    clear(this.hotspotLayer);
    clear(this.characterLayer);
    for (const hs of location.hotspots) {
      const { x, y, w, h: ht } = hs.rect;
      const style = `left:${x}%;top:${y}%;width:${w}%;height:${ht}%`;
      if (hs.kind === "navigate") {
        // Navigate hotspots show a small arrow in the direction of travel instead of a text label. The name stays
        // available as the accessible name, a tooltip (title) and a label shown on keyboard focus.
        const dir = hotspotDirection(hs);
        const icon = hotspotIcon(hs);
        this.hotspotLayer.append(
          h(
            "button",
            {
              class: `hotspot hotspot--navigate hotspot--dir-${dir}${icon ? " has-icon" : ""}`,
              style,
              "aria-label": hs.label,
              title: hs.label,
              "data-hotspot": hs.id,
              "data-direction": dir,
              "data-icon": icon ?? "arrow",
              onclick: () => this.handlers.onHotspot(hs, location)
            },
            icon
              ? h("span", { class: "hotspot-arrow hotspot-icon", "aria-hidden": "true" }, iconSvg(icon), h("span", { class: "hotspot-badge" }, arrowIcon(dir)))
              : h("span", { class: "hotspot-arrow", "aria-hidden": "true" }, arrowIcon(dir)),
            h("span", { class: "hotspot-label", "aria-hidden": "true" }, hs.label)
          )
        );
        continue;
      }
      const icon = hotspotIcon(hs);
      this.hotspotLayer.append(
        h(
          "button",
          {
            class: `hotspot hotspot--${hs.kind}${icon ? " has-icon" : ""}`,
            style,
            "aria-label": hs.label,
            title: hs.label,
            "data-hotspot": hs.id,
            "data-icon": icon ?? "",
            onclick: () => this.handlers.onHotspot(hs, location)
          },
          icon ? h("span", { class: "hotspot-icon", "aria-hidden": "true" }, iconSvg(icon)) : null,
          h("span", { class: "hotspot-label", "aria-hidden": "true" }, hs.label)
        )
      );
    }
    this.stopLive();
    const live = this.opts.reducedMotion ? [] : characters.filter((c) => c.live);
    if (live.length) this.startLive(live);
    for (const c of characters) {
      // Someone already in the still (live clips but no walk-in) is never covered by a placeholder, even with reduced motion.
      const isLive = live.includes(c) || (!!c.live && !c.live.enter);
      const img = isLive ? h("span", { class: "character-hit" }) : h("img", { src: `${this.mediaUrl(c.sprite)}.png`, alt: "", draggable: "false" });
      const btn = h(
        "button",
        {
          class: `character${isLive ? " character--live" : ""}${c.position.flip ? " is-flipped" : ""}`,
          style: `left:${c.position.x}%;top:${c.position.y}%;height:${c.position.height}%`,
          "aria-label": `Talk to ${c.name}, ${c.role}`,
          "data-character": c.id,
          onclick: () => this.handlers.onCharacter(c)
        },
        img,
        h("span", { class: "character-name" }, c.name)
      );
      this.characterLayer.append(btn);
    }
    this.root.classList.remove("is-travelling");
  }

  private clipUrl(base: string): string {
    return `${this.mediaUrl(base)}.${this.opts.supportsWebm ? "webm" : "mp4"}`;
  }

  /** Play the location's enter clip (if any), then loop the idle clip. Both start/end on known frames, so it is seamless. */
  private startLive(chars: Character[]): void {
    const first = chars[0]!.live!;
    const video = h("video", { class: "live-clip", "data-live": chars.map((c) => c.id).join(" ") }) as HTMLVideoElement;
    video.muted = true;
    video.playsInline = true;
    const idle = (): void => {
      video.onended = null;
      video.loop = true;
      video.src = this.clipUrl(first.idle);
      video.dataset.clip = "idle";
      void video.play().catch(() => undefined);
    };
    if (first.enter) {
      video.loop = false;
      video.src = this.clipUrl(first.enter);
      video.dataset.clip = "enter";
      video.onended = idle;
      void video.play().catch(() => idle());
    } else idle();
    video.addEventListener("playing", () => video.classList.add("is-on"), { once: true });
    this.liveLayer.append(video);
    this.live = { video, idle: first.idle, ids: new Set(chars.map((c) => c.id)) };
  }

  /** Play the talking clip once, then return to the idle loop. No-op for characters without live clips. */
  talk(c: Character): void {
    const cur = this.live;
    if (!cur || !cur.ids.has(c.id) || !c.live) return;
    const { video, idle } = cur;
    video.loop = false;
    video.src = this.clipUrl(c.live.talk);
    video.dataset.clip = "talk";
    video.onended = () => {
      video.onended = null;
      video.loop = true;
      video.src = this.clipUrl(idle);
      video.dataset.clip = "idle";
      void video.play().catch(() => undefined);
    };
    void video.play().catch(() => undefined);
  }

  /** Loop the talk clip while speech plays (AI chat); `false` returns to the idle loop. */
  setTalking(c: Character, on: boolean): void {
    const cur = this.live;
    if (!cur || !cur.ids.has(c.id) || !c.live) return;
    const { video } = cur;
    const want = on ? "talk" : "idle";
    if (video.dataset.clip === want) return;
    video.onended = null;
    video.loop = true;
    video.src = this.clipUrl(on ? c.live.talk : cur.idle);
    video.dataset.clip = want;
    void video.play().catch(() => undefined);
  }

  private stopLive(): void {
    if (this.live) {
      this.live.video.pause();
      this.live.video.removeAttribute("src");
      this.live.video.load();
    }
    this.live = null;
    clear(this.liveLayer);
  }

  hideInteractive(): void {
    this.stopLive();
    clear(this.hotspotLayer);
    clear(this.characterLayer);
    this.root.classList.add("is-travelling");
  }
}
